/**
 * useRapportDocumentVereisten
 *
 * Portal side of the pre-inspection document checklist: the opdrachtgever sees
 * which documents the ibedrijf requests for a rapport, uploads files per item
 * (or as extra documents outside the list), and can remove their own uploads.
 * After each upload action the ibedrijf is notified via the notification queue.
 */
import { ref } from "vue";
import { supabase } from "../lib/supabase";
import { resizeImageFile } from "../utils/imageUtils";
import type { RapportDocumentUpload, RapportDocumentVereiste } from "../../../../types";

const USER_FILES_BUCKET = "userFiles";

function sanitizeFileName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]+/g, "_");
}

export function useRapportDocumentVereisten() {
  const vereisten = ref<RapportDocumentVereiste[]>([]);
  const uploads = ref<RapportDocumentUpload[]>([]);
  const loading = ref(false);
  const uploading = ref(false);
  const error = ref<string | null>(null);

  async function load(rapportId: string): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const [vereistenResult, uploadsResult] = await Promise.all([
        supabase
          .from("rapport_document_vereisten")
          .select("*")
          .eq("rapport_id", rapportId)
          .order("sort_order", { ascending: true }),
        supabase
          .from("rapport_document_uploads")
          .select("*")
          .eq("rapport_id", rapportId)
          .order("created_at", { ascending: true }),
      ]);
      if (vereistenResult.error) throw vereistenResult.error;
      if (uploadsResult.error) throw uploadsResult.error;
      vereisten.value = (vereistenResult.data || []) as RapportDocumentVereiste[];
      uploads.value = (uploadsResult.data || []) as RapportDocumentUpload[];
    } catch (err: any) {
      console.error("Error loading document vereisten:", err);
      error.value = err.message || "Documenten konden niet worden geladen";
    } finally {
      loading.value = false;
    }
  }

  /**
   * Upload one or more files against a checklist item (or as extra documents
   * when vereisteId is null) and notify the ibedrijf once for the whole batch.
   */
  async function uploadFiles(rapportId: string, vereisteId: string | null, files: File[]): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Niet ingelogd");

    uploading.value = true;
    try {
      const uploadedIds: string[] = [];
      for (const rawFile of files) {
        // Downscale/recompress images before upload; leave PDFs untouched.
        const file = await resizeImageFile(rawFile);
        const uploadId = crypto.randomUUID();
        const filePath = `rapporten/${rapportId}/documentvereisten/${uploadId}_${sanitizeFileName(file.name)}`;

        const { error: storageError } = await supabase.storage.from(USER_FILES_BUCKET).upload(filePath, file, {
          contentType: file.type || undefined,
        });
        if (storageError) throw new Error("Document uploaden mislukt: " + storageError.message);

        const { error: insertError } = await supabase.from("rapport_document_uploads").insert({
          id: uploadId,
          rapport_id: rapportId,
          vereiste_id: vereisteId,
          file_path: filePath,
          file_name: file.name,
          mime_type: file.type || null,
          file_size: file.size,
          geupload_door: user.id,
          geupload_door_rol: "opdrachtgever",
        } as any);
        if (insertError) {
          await supabase.storage.from(USER_FILES_BUCKET).remove([filePath]);
          throw insertError;
        }
        uploadedIds.push(uploadId);
      }

      if (uploadedIds.length > 0) {
        // One notification per upload action, not per file. Non-fatal: the
        // uploads themselves are already visible to the ibedrijf.
        const { error: notifyError } = await supabase.rpc("enqueue_document_geupload_notification", {
          p_rapport_id: rapportId,
          p_upload_ids: uploadedIds,
        });
        if (notifyError) console.error("Error enqueueing upload notification:", notifyError);
      }

      await load(rapportId);
    } finally {
      uploading.value = false;
    }
  }

  async function deleteUpload(rapportId: string, upload: RapportDocumentUpload): Promise<void> {
    const { error: deleteError } = await supabase.from("rapport_document_uploads").delete().eq("id", upload.id);
    if (deleteError) throw deleteError;
    const { error: storageError } = await supabase.storage.from(USER_FILES_BUCKET).remove([upload.file_path]);
    if (storageError) console.error("Failed to remove uploaded file from storage:", storageError);
    await load(rapportId);
  }

  function getUploadUrl(upload: Pick<RapportDocumentUpload, "file_path">): string {
    const { data } = supabase.storage.from(USER_FILES_BUCKET).getPublicUrl(upload.file_path);
    return data.publicUrl;
  }

  return {
    vereisten,
    uploads,
    loading,
    uploading,
    error,
    load,
    uploadFiles,
    deleteUpload,
    getUploadUrl,
  };
}
